import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { PreInscricaoAgenda, PreInscricaoAgendaRequest } from '../../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../../core/api/pre-inscricao.service';
import { CampoDataComponent } from '../../../shared/campo-data/campo-data.component';
import { formatarDataHoraLocal } from '../../../utils/formatar-data-hora-local';
import { mensagemDeErro } from '../../../utils/mensagem-erro';
import { DialogAlertComponent } from '../../pessoa-turma/turma-pessoa.dialog.component';
import { semHorarioComLugar } from './agenda-disponibilidade';

/**
 * Horários de atendimento de uma campanha, com quantos inscritos cabem em cada um.
 *
 * A família não escolhe horário: a API põe cada inscrito no horário futuro mais cedo que ainda
 * tem lugar (`PRE-14` da igreja-api). Sem horário futuro com lugar, **todo envio é recusado**
 * (`PRE-15`) — por isso o aviso no topo não é decoração.
 *
 * Recusas que chegam como `400` com a mensagem do backend: data e hora repetida na campanha,
 * limite abaixo dos já agendados, e excluir horário que já tem inscrito.
 */
@Component({
  selector: 'app-pre-inscricao-agendas-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    CampoDataComponent,
  ],
  templateUrl: './pre-inscricao-agendas.dialog.html',
  styleUrl: './pre-inscricao-agendas.dialog.scss',
})
export class PreInscricaoAgendasDialogComponent implements OnInit {
  @Input() campanhaId = '';
  @Input() campanhaNome?: string;

  agendas: PreInscricaoAgenda[] = [];
  carregando = false;
  carregou = false;
  salvando = false;

  editandoId: string | null = null;
  /** `yyyy-MM-dd`, do `app-campo-data`. */
  data = '';
  /** `HH:mm`, do `input type="time"`. */
  hora = '';
  limiteAtendidos: number | null = null;

  /** `dataHora` do horário em edição, como estava antes: é o que diz se a edição remarca alguém. */
  private dataHoraOriginal = '';
  private agendadosDoEditado = 0;

  constructor(
    private ref: NbDialogRef<PreInscricaoAgendasDialogComponent>,
    private service: PreInscricaoService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  /** `PRE-15` — sem horário futuro com lugar, o link público recusa todo mundo. */
  get semLugar(): boolean {
    return this.carregou && semHorarioComLugar(this.agendas);
  }

  /** Mudar a data ou a hora de quem já tem inscrito remarca essas pessoas, sem e-mail novo. */
  get remarcaInscritos(): boolean {
    return !!this.editandoId && this.agendadosDoEditado > 0 && this.dataHoraDoFormulario() !== this.dataHoraOriginal;
  }

  get agendadosEmEdicao(): number {
    return this.agendadosDoEditado;
  }

  jaPassou(a: PreInscricaoAgenda): boolean {
    return !!a.dataHora && new Date(a.dataHora).getTime() <= Date.now();
  }

  carregar(): void {
    if (!this.campanhaId) return;
    this.carregando = true;
    this.service
      .listarAgendas(this.campanhaId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (as) => {
          this.agendas = as ?? [];
          this.carregou = true;
        },
        error: (err) => {
          this.agendas = [];
          this.carregou = false;
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar os horários.'), 'Erro');
        },
      });
  }

  editar(a: PreInscricaoAgenda): void {
    this.editandoId = a.id ?? null;
    const [data, hora] = (a.dataHora ?? '').split('T');
    this.data = data ?? '';
    this.hora = (hora ?? '').slice(0, 5);
    this.limiteAtendidos = a.limiteAtendidos ?? null;
    this.dataHoraOriginal = this.dataHoraDoFormulario();
    this.agendadosDoEditado = a.agendados ?? 0;
  }

  cancelarEdicao(): void {
    this.editandoId = null;
    this.limparFormulario();
  }

  salvar(): void {
    if (!this.data || !this.hora || this.limiteAtendidos == null) {
      this.toastr.warning('Data, hora e limite de atendidos são obrigatórios.', 'Campos obrigatórios');
      return;
    }
    if (this.limiteAtendidos < 1) {
      this.toastr.warning('O limite de atendidos é pelo menos 1.', 'Limite inválido');
      return;
    }

    const corpo: PreInscricaoAgendaRequest = {
      dataHora: this.dataHoraDoFormulario(),
      limiteAtendidos: this.limiteAtendidos,
    };
    const editando = this.editandoId;

    this.salvando = true;
    const req = editando
      ? this.service.atualizarAgenda(this.campanhaId, editando, { ...corpo, id: editando })
      : this.service.criarAgenda(this.campanhaId, corpo);

    req.pipe(finalize(() => (this.salvando = false))).subscribe({
      next: () => {
        this.toastr.success(
          editando ? `Horário de ${this.rotulo(corpo.dataHora)} atualizado.` : `Horário de ${this.rotulo(corpo.dataHora)} adicionado.`,
          'Horários de atendimento',
        );
        this.editandoId = null;
        this.limparFormulario();
        this.carregar();
      },
      error: (err) => this.toastr.danger(mensagemDeErro(err, 'Não foi possível salvar o horário.'), 'Erro'),
    });
  }

  /** `MSG-08` — exclusão confirma citando o horário. Horário com inscrito a API recusa. */
  excluir(a: PreInscricaoAgenda): void {
    if (!a.id) return;
    const rotulo = this.rotulo(a.dataHora);
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Excluir horário',
          message: `Confirma a exclusão do horário de ${rotulo}?`,
        },
      })
      .onClose.subscribe((confirmou) => {
        if (!confirmou) return;
        this.service.excluirAgenda(this.campanhaId, a.id!).subscribe({
          next: () => {
            this.toastr.success(`Horário de ${rotulo} excluído.`, 'Horários de atendimento');
            this.carregar();
          },
          error: (err) => this.toastr.danger(mensagemDeErro(err, 'Não foi possível excluir o horário.'), 'Erro'),
        });
      });
  }

  fechar(): void {
    this.ref.close();
  }

  /** "03/10/2026 às 09:00" — o mesmo formato da tela de sucesso pública. */
  rotulo(dataHora?: string): string {
    return formatarDataHoraLocal(dataHora);
  }

  /**
   * `FRM-08` — a tela pede data e hora separadas, e a API guarda as duas juntas, **sem fuso**
   * (horário local da paróquia): `2026-10-03T09:00:00`. A junção acontece aqui, no formulário.
   */
  private dataHoraDoFormulario(): string {
    return this.data && this.hora ? `${this.data}T${this.hora}:00` : '';
  }

  private limparFormulario(): void {
    this.data = '';
    this.hora = '';
    this.limiteAtendidos = null;
    this.dataHoraOriginal = '';
    this.agendadosDoEditado = 0;
  }
}
